import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { RetentionCohort } from '@/hooks/useMarketplaceAnalytics';

interface RetentionCohortChartProps {
  title: string;
  description: string;
  cohorts: RetentionCohort[];
  isLoading: boolean;
}

export function RetentionCohortChart({
  title,
  description,
  cohorts,
  isLoading,
}: RetentionCohortChartProps) {
  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{title}</CardTitle>
          <CardDescription>{description}</CardDescription>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-48 w-full" />
        </CardContent>
      </Card>
    );
  }

  const getRetentionColor = (percent: number) => {
    if (percent >= 70) return 'bg-green-500/80 text-white';
    if (percent >= 50) return 'bg-green-500/60 text-white';
    if (percent >= 30) return 'bg-green-500/40 text-foreground';
    if (percent >= 15) return 'bg-green-500/20 text-foreground';
    return 'bg-muted text-muted-foreground';
  };

  const maxMonths = Math.max(...cohorts.map(c => c.retentionByMonth.length), 0);
  const monthHeaders = Array.from({ length: maxMonths }, (_, i) => `M${i + 1}`);

  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent>
        {cohorts.length === 0 || cohorts.every(c => c.signupCount === 0) ? (
          <div className="text-center py-8 text-muted-foreground">
            No cohort data available
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr>
                  <th className="text-left p-2 font-medium text-muted-foreground">Cohort</th>
                  <th className="text-center p-2 font-medium text-muted-foreground">Signups</th>
                  {monthHeaders.map((month) => (
                    <th key={month} className="text-center p-2 font-medium text-muted-foreground">
                      {month}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {cohorts.map((cohort) => (
                  <tr key={cohort.cohortMonth}>
                    <td className="p-2 font-medium">{cohort.cohortMonth}</td>
                    <td className="p-2 text-center">{cohort.signupCount}</td>
                    {monthHeaders.map((_, monthIndex) => {
                      const retention = cohort.retentionByMonth[monthIndex];
                      if (!retention) {
                        return (
                          <td key={monthIndex} className="p-1">
                            <div className="h-12 flex items-center justify-center text-muted-foreground">
                              —
                            </div>
                          </td>
                        );
                      }
                      return (
                        <td key={monthIndex} className="p-1">
                          <div
                            className={cn(
                              "h-12 rounded flex flex-col items-center justify-center",
                              getRetentionColor(retention.percent)
                            )}
                          >
                            <span className="font-medium">{retention.percent}%</span>
                            <span className="text-xs opacity-75">({retention.count})</span>
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        
        {/* Legend */}
        <div className="mt-4 pt-4 border-t flex items-center gap-4 text-xs text-muted-foreground">
          <span>Retention:</span>
          <div className="flex items-center gap-1">
            <div className="w-4 h-4 rounded bg-green-500/80" />
            <span>≥70%</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-4 h-4 rounded bg-green-500/60" />
            <span>≥50%</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-4 h-4 rounded bg-green-500/40" />
            <span>≥30%</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-4 h-4 rounded bg-green-500/20" />
            <span>≥15%</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-4 h-4 rounded bg-muted" />
            <span>&lt;15%</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
